import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { launchOnPumpfun } from "@/lib/wallet";
import CampaignsPanel from "@/components/CampaignsPanel";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { VENUE_META, STATUS_META, shortHash, timeAgo, PLATFORM_META, logoUrl, DEST_META } from "@/lib/meta";
import {
  ArrowLeft, Pause, Play, Send, Sparkles, Flame, ExternalLink, Loader2, Twitter, MessageCircle,
  BadgeCheck, Wallet, Clock, Trophy, Rocket, Pencil, Check, X as XIcon,
} from "lucide-react";

const TABS = ["Overview", "Content", "Campaigns", "Activity", "Fees", "Burns"];
const levelColor = { juice: "#FFC83B", pulp: "#10E396", ai: "#FF9D00" };
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const platIcon = { x: Twitter, telegram: Send, discord: MessageCircle };

const countdown = (iso) => {
  if (!iso) return "—";
  const s = Math.floor((new Date(iso).getTime() - Date.now()) / 1000);
  if (s <= 0) return "expired";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  return h > 0 ? `${h}h ${m}m` : `${m}m ${sec}s`;
};

const EditablePost = ({ p, launchId, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(p.content);
  const [saving, setSaving] = useState(false);
  const canEdit = p.status !== "posted";

  const save = async () => {
    const text = val.trim();
    if (!text) { toast.error("Content can't be empty."); return; }
    if (text === p.content) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/launches/${launchId}/posts/${p.id}`, { content: text });
      toast.success("Post updated.");
      setEditing(false);
      onSaved?.(p.id, data.content);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save edit."); }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="w-full space-y-1.5" data-testid={`post-edit-${p.id}`}>
        <Textarea
          value={val}
          onChange={(e) => setVal(e.target.value)}
          rows={3}
          autoFocus
          className="resize-none border-ai/40 bg-ink text-xs leading-snug text-cream"
          data-testid={`post-edit-input-${p.id}`}
        />
        <div className="flex items-center gap-1.5">
          <Button size="sm" onClick={save} disabled={saving} className="h-6 gap-1 bg-juice px-2 text-[10px] font-semibold text-ink hover:bg-juice/90" data-testid={`post-edit-save-${p.id}`}>
            {saving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Save
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setVal(p.content); setEditing(false); }} className="h-6 gap-1 px-2 text-[10px] text-dust hover:text-cream" data-testid={`post-edit-cancel-${p.id}`}>
            <XIcon size={10} /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group/edit relative w-full pr-5">
      <p className="text-cream">
        {p.content}
        {p.edited && <span className="ml-1 align-middle text-[9px] uppercase tracking-wide text-dust">· edited</span>}
      </p>
      {canEdit && (
        <button
          type="button"
          onClick={() => { setVal(p.content); setEditing(true); }}
          data-testid={`post-edit-btn-${p.id}`}
          title="Edit before it posts"
          className="absolute right-0 top-0 rounded-md p-1 text-dust opacity-0 transition-opacity hover:text-ai group-hover/edit:opacity-100"
        >
          <Pencil size={11} />
        </button>
      )}
    </div>
  );
};

export default function LaunchDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [fees, setFees] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [tab, setTab] = useState("Overview");
  const [busy, setBusy] = useState(false);
  const [pumping, setPumping] = useState(false);
  const launchPump = async () => {
    setPumping(true);
    try {
      const res = await launchOnPumpfun(id);
      if (res.mode === "simulated") toast.message("Pump.fun (simulated)", { description: res.message });
      else toast.success("Launched on Pump.fun", { description: `Tx ${res.signature?.slice(0, 10)}…`, action: res.explorer_url ? { label: "Solscan", onClick: () => window.open(res.explorer_url, "_blank") } : undefined });
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Launch failed."); }
    finally { setPumping(false); }
  };
  const [, setTick] = useState(0);

  const load = useCallback(() => {
    api.get(`/launches/${id}`).then(({ data }) => setD(data)).catch(() => {});
    api.get(`/launches/${id}/fees`).then(({ data }) => setFees(data)).catch(() => {});
    api.get(`/launches/${id}/metrics`).then(({ data }) => setMetrics(data)).catch(() => {});
    api.get(`/launches/${id}/burn-ledger`).then(({ data }) => setLedger(data)).catch(() => {});
    api.get(`/launches/${id}/leaderboard`).then(({ data }) => setLeaderboard(data)).catch(() => {});
    api.get(`/launches/${id}/milestones`).then(({ data }) => setMilestones(data.milestones || [])).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const t = setInterval(() => api.get(`/launches/${id}/burn-ledger`).then(({ data }) => setLedger(data)).catch(() => {}), 15000);
    return () => clearInterval(t);
  }, [id]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (!d) return <div className="p-8 text-dust">Loading command center…</div>;
  const { launch: l, posts, activity, burns } = d;
  const v = VENUE_META[l.venue] || {};
  const st = STATUS_META[l.status] || STATUS_META.draft;
  const pct = l.budget_total ? Math.round((l.budget_spent / l.budget_total) * 100) : 0;
  const logo = logoUrl(l.logo);
  const dest = l.payout_destination || { type: "wallet" };

  const doAction = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); load(); }
    catch { toast.error("Something went wrong."); }
    finally { setBusy(false); }
  };

  const pause = () => doAction(() => api.post(`/launches/${id}/pause`), l.status === "paused" ? "Engine resumed." : "Engine paused.");
  const postNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/launches/${id}/post-now`);
      toast.success(data.message || (data.posted ? "Agent posted." : "Draft queued."));
      load();
    } catch { toast.error("Something went wrong."); }
    finally { setBusy(false); }
  };
  const regen = () => doAction(() => api.post(`/launches/${id}/generate-content`), "Fresh posts drafted.");
  const handlePostSaved = (postId, content) => {
    setD((prev) => prev ? { ...prev, posts: prev.posts.map((x) => x.id === postId ? { ...x, content, edited: true } : x) } : prev);
  };
  const proveHandle = async () => {
    setBusy(true);
    try {
      const { data: cfg } = await api.get("/auth/x/config");
      if (cfg.enabled && dest.type === "x") {
        const { data } = await api.get(`/auth/x/authorize`, { params: { launch_id: id } });
        if (data.url) { window.open(data.url, "_blank", "width=600,height=760"); toast.info("Complete verification in the X window, then refresh."); setBusy(false); return; }
      }
      await api.post(`/launches/${id}/bind-destination`, {});
      toast.success("Handle proven, claim wallet bound.");
      load();
    } catch (e) { toast.error("Could not start verification."); }
    finally { setBusy(false); }
  };
  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/launches/${id}/claim`);
      toast.success(data.claimed ? `Claimed ${data.amount} USDC across ${data.claimed} lot(s).` : "No claimable lots right now.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Claim failed."); }
    finally { setBusy(false); }
  };

  const today = posts.slice(0, 4);

  return (
    <div className="mx-auto max-w-6xl px-5 py-8 lg:px-8">
      <Link to="/app/launches" className="mb-4 inline-flex items-center gap-1 text-sm text-dust hover:text-cream"><ArrowLeft size={15} /> Launches</Link>

      {/* header */}
      <div className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          {logo ? (
            <img src={logo} alt="logo" className="h-12 w-12 rounded-xl border border-line object-cover" data-testid="header-logo" />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-raised" style={{ color: v.color }}><Flame size={22} /></div>
          )}
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xl text-cream">${l.ticker}</span>
              <span className={`rounded-full border px-2.5 py-0.5 text-xs ${st.cls}`} data-testid="launch-status">{st.label}</span>
              <span className="rounded-full border border-pulp/30 bg-pulp/10 px-2.5 py-0.5 text-xs text-pulp" data-testid="desk-chip">{({ "": "No desk", spark: "Starter", week: "Medium", month: "Full" }[l.pack || ""] || "No desk")} desk</span>
            </div>
            <p className="text-sm text-dust">{l.name} · {v.name}</p>
            {l.contract_address && (
              <a href={`https://${v.explorer}/token/${l.contract_address}`} target="_blank" rel="noreferrer" className="mt-1 flex items-center gap-1 font-mono text-[11px] text-ai hover:underline">
                {shortHash(l.contract_address)} <ExternalLink size={11} />
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={pause} disabled={busy} variant="outline" className="gap-1.5 border-line bg-raised hover:bg-line/40" data-testid="btn-pause">
            {l.status === "paused" ? <><Play size={15} /> Resume</> : <><Pause size={15} /> Pause</>}
          </Button>
          <Button onClick={postNow} disabled={busy} variant="outline" className="gap-1.5 border-line bg-raised hover:bg-line/40" data-testid="btn-post-now">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Post now
          </Button>
          <Link to="/app/billing"><Button className="gap-1.5 bg-juice text-ink hover:bg-juice/90 font-semibold" data-testid="btn-add-juice"><Sparkles size={15} /> Add fuel</Button></Link>
          {l.venue === "pumpfun" && !["live", "paused"].includes(l.status) && (
            <Button onClick={launchPump} disabled={pumping} className="gap-1.5 bg-pulp text-ink hover:bg-pulp/90 font-semibold" data-testid="btn-pumpfun-launch">
              {pumping ? <Loader2 size={15} className="animate-spin" /> : <Rocket size={15} />} Launch on Pump.fun
            </Button>
          )}
        </div>
      </div>

      {/* tabs */}
      <div className="mt-6 flex gap-1 border-b border-line">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
            className={`px-4 py-2.5 text-sm transition-colors ${tab === t ? "border-b-2 border-juice text-cream" : "text-dust hover:text-cream"}`}>{t}</button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "Overview" && (
          <div className="space-y-6">
            {metrics && (
              <div data-testid="metrics-strip">
                <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-dust">
                  <span>Token metrics</span>
                  {metrics.onchain?.simulated && <span className="rounded bg-ai/15 px-1.5 py-0.5 text-[9px] text-ai">simulated · live on RPC + indexer</span>}
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
                  {[
                    ["Price", `$${metrics.onchain.price_usd}`, "#FAF6EE"],
                    ["Market cap", `$${Math.round(metrics.onchain.market_cap).toLocaleString()}`, "#FAF6EE"],
                    ["24h", `${metrics.onchain.change_24h > 0 ? "+" : ""}${metrics.onchain.change_24h}%`, metrics.onchain.change_24h >= 0 ? "#10E396" : "#FF3B5C"],
                    ["24h vol", `$${Math.round(metrics.onchain.volume_24h).toLocaleString()}`, "#FAF6EE"],
                    ["Liquidity", `$${Math.round(metrics.onchain.liquidity_usd).toLocaleString()}`, "#FAF6EE"],
                    ["Holders", metrics.onchain.holders.toLocaleString(), "#FAF6EE"],
                    ["Fees (USDC)", `$${metrics.fees_usdc}`, "#FFC83B"],
                    ["$MOON burned", metrics.juice_burned.toLocaleString(), "#10E396"],
                  ].map(([label, val, c]) => (
                    <div key={label} className="rounded-xl border border-line bg-surface p-3">
                      <p className="text-[10px] uppercase tracking-wider text-dust">{label}</p>
                      <p className="mt-1 font-mono text-sm" style={{ color: c }}>{val}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    ["Claimable now", `$${metrics.accrued_pending} USDC`],
                    ["Claimed", `$${metrics.claimed} USDC`],
                    ["Expired→burned", `$${metrics.expired_burned} USDC`],
                    ["Posts", `${metrics.posts_posted}/${metrics.posts_total} · ${metrics.days_remaining}d left`],
                  ].map(([label, val]) => (
                    <div key={label} className="rounded-xl border border-line bg-raised/40 p-3">
                      <p className="text-[10px] uppercase tracking-wider text-dust">{label}</p>
                      <p className="mt-1 font-mono text-sm text-cream">{val}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {ledger && (
              <div className="rounded-2xl border border-line bg-surface p-5" data-testid="burn-ledger">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="flex items-center gap-2 text-sm font-semibold">
                    <Flame size={15} className="text-pulp" /> Fees → $MOON burn
                    <span className="h-1.5 w-1.5 rounded-full bg-pulp animate-soft-pulse" title="live" />
                  </h2>
                  <span className="font-mono text-xs text-dust" data-testid="burn-ledger-total">${ledger.total_usd} burned</span>
                </div>
                <div className="flex h-2.5 overflow-hidden rounded-full bg-raised">
                  <div className="h-full bg-pulp transition-all" style={{ width: `${ledger.total_usd ? ledger.protocol_pct : 100}%` }} data-testid="ledger-protocol-bar" />
                  <div className="h-full bg-ai transition-all" style={{ width: `${ledger.expired_pct}%` }} data-testid="ledger-expired-bar" />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-line bg-raised/40 p-3">
                    <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-dust"><span className="h-2 w-2 rounded-full bg-pulp" /> Protocol ({ledger.total_usd ? ledger.protocol_pct : 100}%)</p>
                    <p className="mt-1 font-mono text-lg" style={{ color: "#10E396" }} data-testid="ledger-protocol-usd">${ledger.protocol_usd}</p>
                    <p className="text-[10px] text-dust/70">15% of each lot, on arrival</p>
                  </div>
                  <div className="rounded-xl border border-line bg-raised/40 p-3">
                    <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-dust"><span className="h-2 w-2 rounded-full bg-ai" /> Expired ({ledger.expired_pct}%)</p>
                    <p className="mt-1 font-mono text-lg" style={{ color: "#FF9D00" }} data-testid="ledger-expired-usd">${ledger.expired_usd}</p>
                    <p className="text-[10px] text-dust/70">Unclaimed 70% after 7 days</p>
                  </div>
                </div>
                <div className="mt-3 max-h-44 overflow-y-auto">
                  {ledger.rows.length ? ledger.rows.map((b) => (
                    <div key={b.id} className="flex items-center justify-between border-b border-line/50 py-2 text-xs last:border-0" data-testid={`ledger-row-${b.id}`}>
                      <span className="flex items-center gap-1.5">
                        <span className={`h-1.5 w-1.5 rounded-full ${b.source === "expired" ? "bg-ai" : "bg-pulp"}`} />
                        <span className="font-medium text-cream">{b.source} · buy+burn $MOON</span>
                        <span className="text-dust">{timeAgo(b.created_at)}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-cream">${b.amount_usd}</span>
                        {b.tx_hash && <span className="font-mono text-[10px] text-dust">{shortHash(b.tx_hash)}</span>}
                      </span>
                    </div>
                  )) : <p className="py-2 text-xs text-dust">No burns yet — creator-fee lots seed the $MOON burn.</p>}
                </div>
                <p className="mt-2 text-[11px] text-dust/70">15% of every lot burns $MOON on arrival. Unclaimed 70% burns after 7 days.</p>
              </div>
            )}
            {(leaderboard || milestones.length > 0) && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {leaderboard && (
                  <div className="rounded-2xl border border-line bg-surface p-5" data-testid="cc-leaderboard">
                    <div className="mb-3 flex items-center justify-between">
                      <h2 className="flex items-center gap-2 text-sm font-semibold"><Trophy size={15} className="text-juice" /> Top buys today</h2>
                      <span className="font-mono text-xs text-dust">${leaderboard.total_usd?.toLocaleString?.() ?? leaderboard.total_usd} · {leaderboard.count}</span>
                    </div>
                    {leaderboard.top?.length ? leaderboard.top.slice(0, 5).map((b, i) => (
                      <div key={i} className="flex items-center justify-between border-b border-line/50 py-1.5 text-xs last:border-0" data-testid={`cc-leaderboard-row-${i}`}>
                        <span className="flex items-center gap-1.5"><span className="w-4 text-center">{["🥇", "🥈", "🥉"][i] || "🔹"}</span><span className="font-mono text-cream">{b.buyer}</span>{b.simulated && <span className="rounded bg-ink px-1 text-[9px] uppercase text-dust">sim</span>}</span>
                        <span className="font-mono text-pulp">${b.usd?.toLocaleString?.() ?? b.usd}</span>
                      </div>
                    )) : <p className="py-2 text-xs text-dust">{leaderboard.detection_live ? "No buys yet today." : "Detection off — connect Helius, or simulate a buy from the Telegram page."}</p>}
                  </div>
                )}
                {milestones.length > 0 && (
                  <div className="rounded-2xl border border-line bg-surface p-5" data-testid="cc-milestones">
                    <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Sparkles size={15} className="text-pulp" /> Milestones</h2>
                    <div className="flex flex-wrap gap-2">
                      {milestones.slice(0, 10).map((m) => (
                        <a key={m.id} href={`/m/${m.id}`} target="_blank" rel="noreferrer" className="rounded-full border border-line bg-raised px-2.5 py-1 font-mono text-[11px] text-cream transition-colors hover:border-juice hover:text-juice" data-testid={`cc-milestone-${m.id}`} title="Open shareable card">
                          {m.cat === "burn" ? "🔥" : m.cat === "mcap" ? "📈" : "🟢"} ${m.tier?.toLocaleString?.() ?? m.tier} ↗
                        </a>
                      ))}
                    </div>
                    <p className="mt-2 text-[10px] text-dust/60">Auto-posted to the official Wen Moon channel when crossed.</p>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              <div className="rounded-2xl border border-line bg-surface p-5">
                <h2 className="mb-3 text-sm font-semibold">What the agents are doing today</h2>
                <ul className="space-y-2">
                  {today.map((p) => {
                    const Icon = platIcon[p.platform] || Send;
                    return (
                      <li key={p.id} className="flex items-start gap-3 rounded-lg bg-raised/40 p-3 text-sm">
                        <Icon size={15} className="mt-0.5 shrink-0 text-ai" />
                        <EditablePost p={p} launchId={id} onSaved={handlePostSaved} />
                      </li>
                    );
                  })}
                  {!today.length && <li className="text-sm text-dust">No posts drafted yet.</li>}
                </ul>
              </div>
              <div className="rounded-2xl border border-line bg-surface p-5">
                <h2 className="mb-3 text-sm font-semibold">Socials</h2>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  {["x", "telegram", "discord"].map((k) => {
                    const s = l.socials?.[k];
                    const txt = s ? (typeof s === "string" ? s : (s.raw || s.url || s.canonical)) : "—";
                    return (
                      <div key={k} className="rounded-lg bg-raised/40 p-3" data-testid={`social-cell-${k}`}>
                        <p className="text-xs uppercase tracking-wider text-dust">{PLATFORM_META[k].label}</p>
                        <p className="mt-1 truncate font-mono text-xs text-cream">{txt}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="rounded-2xl border border-line bg-surface p-5">
                <p className="text-xs uppercase tracking-wider text-dust">Budget</p>
                <p className="mt-1 font-mono text-xl text-cream">${l.budget_spent} <span className="text-dust">/ ${l.budget_total}</span></p>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-raised"><div className="h-full bg-juice" style={{ width: `${pct}%` }} /></div>
              </div>
              <div className="rounded-2xl border border-line bg-surface p-5">
                <p className="text-xs uppercase tracking-wider text-dust">Voice / goal</p>
                <p className="mt-1 text-sm capitalize text-cream">{l.voice} · {l.goal}</p>
                <p className="mt-2 text-xs text-dust">{l.description}</p>
              </div>
            </div>
            </div>
          </div>
        )}

        {tab === "Campaigns" && <CampaignsPanel launchId={id} launch={l} />}

        {tab === "Content" && (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold">Week calendar</h2>
              <Button onClick={regen} disabled={busy} variant="outline" size="sm" className="gap-1.5 border-ai/40 bg-ai/10 text-ai hover:bg-ai/20" data-testid="btn-regen-content">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Regenerate with AI
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {DAYS.slice(0, 7).map((day, di) => {
                const dayPosts = posts.filter((p) => p.day === di);
                return (
                  <div key={day} className="rounded-xl border border-line bg-surface p-3" data-testid={`calendar-day-${di}`}>
                    <p className="mb-2 text-xs font-medium uppercase tracking-wider text-dust">{day}</p>
                    <div className="space-y-2">
                      {dayPosts.map((p) => {
                        const Icon = platIcon[p.platform] || Send;
                        return (
                          <div key={p.id} className={`rounded-lg border p-2.5 text-xs leading-snug ${p.status === "posted" ? "border-pulp/30 bg-pulp/5" : "border-line bg-raised/40"}`}>
                            <div className="mb-1 flex items-center gap-1.5 text-ai"><Icon size={11} /><span className="text-[10px] uppercase">{PLATFORM_META[p.platform]?.label}</span>{p.status === "posted" && <span className="ml-auto text-[10px] text-pulp">posted</span>}</div>
                            <div className="flex gap-2">
                              {logo && <img src={logo} alt="" className="mt-0.5 h-8 w-8 shrink-0 rounded-md border border-line object-cover" />}
                              <EditablePost p={p} launchId={id} onSaved={handlePostSaved} />
                            </div>
                          </div>
                        );
                      })}
                      {!dayPosts.length && <p className="text-[11px] text-dust/50">—</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "Activity" && (
          <div className="rounded-2xl border border-line bg-surface p-5" data-testid="launch-activity">
            <div className="space-y-3 font-mono text-sm">
              {activity.map((e) => (
                <div key={e.id} className="flex items-start gap-3 border-b border-line/40 pb-3 last:border-0">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: levelColor[e.level] || "#A19A8A" }} />
                  <div className="flex-1"><p className="text-cream">{e.message}</p><p className="text-[11px] text-dust">{timeAgo(e.created_at)}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Fees" && fees && (
          <div className="space-y-6" data-testid="fees-tab">
            <div className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-raised" style={{ color: DEST_META[dest.type]?.color }}>
                  {dest.type === "wallet" ? <Wallet size={18} /> : dest.type === "x" ? <Twitter size={18} /> : <Send size={18} />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-cream">{DEST_META[dest.type]?.label}</span>
                    {dest.type !== "wallet" && <span className="font-mono text-xs text-dust">{dest.ref}</span>}
                    {dest.verified ? <BadgeCheck size={14} className="text-pulp" /> : <span className="rounded bg-warn/15 px-1.5 py-0.5 text-[10px] text-warn">unverified</span>}
                  </div>
                  <p className="text-xs text-dust">{dest.type === "wallet" ? "Your 70% swept to wallet as USDC. 15% protocol · 15% buy+burn $MOON." : "70% you · 15% protocol · 15% $MOON burn per lot. Your 70% is claimable 7 days, then bridged to 4663 & burned."}</p>
                </div>
              </div>
              {dest.type !== "wallet" && (
                <div className="flex flex-col items-end gap-2">
                  <div className="flex gap-2">
                    {!dest.verified && <Button onClick={proveHandle} disabled={busy} variant="outline" className="gap-1.5 border-line bg-raised hover:bg-line/40" data-testid="btn-prove-handle"><BadgeCheck size={15} /> Prove handle</Button>}
                    <Button onClick={claim} disabled={busy || !dest.verified} title={!dest.verified ? "Verify the handle and bind a wallet before claiming" : ""} className="gap-1.5 bg-juice text-ink hover:bg-juice/90 font-semibold disabled:opacity-40" data-testid="btn-claim"><Sparkles size={15} /> Claim</Button>
                  </div>
                  {!dest.verified && <span className="text-[10px] text-warn" data-testid="claim-locked-hint">Claim locked until proven + bound</span>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {[
                ["Accrued pending", fees.summary.accrued_pending, "#F59E0B"],
                ["Claimable now", fees.summary.claimable_now, "#FFC83B"],
                ["Claimed", fees.summary.claimed, "#FAF6EE"],
                ["Expired → burned", fees.summary.expired_burned, "#10E396"],
              ].map(([lbl, val, c]) => (
                <div key={lbl} className="rounded-2xl border border-line bg-surface p-4">
                  <p className="text-xs uppercase tracking-wider text-dust">{lbl}</p>
                  <p className="mt-1 font-mono text-lg" style={{ color: c }}>{val} <span className="text-xs text-dust">USDC</span></p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-line bg-raised/40 px-4 py-3 text-sm" data-testid="next-expiry">
              <Clock size={15} className="text-warn" />
              <span className="text-dust">Next lot expiry:</span>
              <span className="font-mono text-cream">{countdown(fees.summary.next_expiry)}</span>
              {fees.summary.normalizing > 0 && <span className="ml-2 rounded bg-juice/15 px-1.5 py-0.5 text-[10px] uppercase text-juice">{fees.summary.normalizing} normalizing → USDC</span>}
              <span className="ml-auto text-[11px] text-dust">Expired lots bridge to Robinhood Chain ({fees.summary.chain_id}) & buy+burn $MOON</span>
            </div>

            <div className="overflow-hidden rounded-2xl border border-line bg-surface" data-testid="fee-lots-table">
              <div className="grid grid-cols-[1fr_1.2fr_1fr_0.9fr_0.8fr] gap-2 border-b border-line px-5 py-3 text-[11px] uppercase tracking-wider text-dust">
                <span>Lot</span><span>Gross (USDC)</span><span>Status</span><span>Deadline</span><span className="text-right">Received</span>
              </div>
              {fees.lots.map((lot) => {
                const stColor = { pending: "text-warn", pending_normalize: "text-ai", claimed: "text-pulp", expired_to_burn: "text-danger", burned: "text-danger" }[lot.status] || "text-dust";
                return (
                  <div key={lot.id} className="grid grid-cols-[1fr_1.2fr_1fr_0.9fr_0.8fr] items-center gap-2 border-b border-line/60 px-5 py-3 font-mono text-xs last:border-0">
                    <span className="text-dust">{lot.lot_id}</span>
                    <span className="text-cream">
                      {lot.status === "pending_normalize" ? "—" : `$${(lot.gross_usdc || lot.amount || 0).toFixed(2)}`}
                      <span className="ml-1 text-[10px] text-dust/70">({lot.source_amount} {lot.source_quote})</span>
                      {lot.status !== "pending_normalize" && lot.protocol_usdc > 0 && (
                        <span className="mt-0.5 block text-[9px] leading-tight text-dust/60">creator ${(lot.creator_usdc || lot.amount || 0).toFixed(2)} · proto ${(lot.protocol_usdc || 0).toFixed(2)} · burn ${(lot.moon_burn_usdc || 0).toFixed(2)}</span>
                      )}
                    </span>
                    <span className={stColor}>{lot.status.replace(/_/g, " ")}</span>
                    <span className="text-dust">{lot.status === "pending" ? countdown(lot.claim_deadline) : "—"}</span>
                    <span className="text-right text-dust">{lot.received_at ? timeAgo(lot.received_at) : "normalizing"}</span>
                  </div>
                );
              })}
              {!fees.lots.length && <p className="p-5 text-sm text-dust">No fee lots yet.</p>}
            </div>
          </div>
        )}

        {tab === "Burns" && (
          <div className="overflow-hidden rounded-2xl border border-line bg-surface" data-testid="launch-burns">
            {burns.length === 0 && <p className="p-5 text-sm text-dust">No burns yet.</p>}
            {burns.map((b) => (
              <div key={b.id} className="grid grid-cols-[1fr_1.4fr_1.4fr_0.7fr] items-center gap-2 border-b border-line/60 px-5 py-3 font-mono text-sm animate-pulp-flash last:border-0">
                <span className={b.kind === "juice" ? "text-juice" : "text-pulp"}>${b.token}</span>
                <span className="flex items-center gap-2 text-cream">${b.amount_usd.toFixed(2)}
                  {b.source === "expired_creator_fees" && <span className="rounded bg-pulp/15 px-1.5 py-0.5 text-[9px] uppercase text-pulp">expired fees</span>}
                </span>
                <a href={`${b.explorer ? b.explorer : `https://${v.explorer}`}/tx/${b.tx_hash}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-ai hover:underline">
                  {shortHash(b.tx_hash)} <ExternalLink size={11} />
                  {b.simulated && <span className="rounded bg-ai/15 px-1.5 py-0.5 text-[9px] uppercase text-ai">simulated</span>}
                </a>
                <span className="text-right text-xs text-dust">{timeAgo(b.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
